import { useEffect, useMemo, useRef, useState } from 'react'

import { AuditTabs } from '@/components/audit/AuditTabs'
import { EMPTY_FILTERS, FilterBar, type Filters } from '@/components/audit/FilterBar'
import { DataTable, type Column, type Sort } from '@/components/data/DataTable'
import { LoadingRows } from '@/components/data/SearchInput'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { type AuditStats, fetchAuditStats, fmtDuration, statusTone } from '@/lib/audit'

type OpRow = AuditStats['top_operations'][number]
type SortKey = 'count' | 'success' | 'failed' | 'avg_latency_ms' | 'operation'

const fmt = (n: number) => n.toLocaleString()

/** Static counters in one row — the same look as section tiles, but not tabs. */
function StatRow({ items }: { items: { label: string; value: string; meta?: string; tone?: string }[] }) {
  return (
    <dl className="grid shrink-0 border-b border-line" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map((it) => (
        <div key={it.label} className="min-w-0 border-r border-line px-3.5 py-2 last:border-r-0">
          <dd className={`truncate font-mono text-[18px] leading-6 ${it.tone ?? 'text-foreground'}`}>{it.value}</dd>
          <dt className="truncate text-[12.5px] text-text-2">{it.label}</dt>
          {it.meta && <dd className="truncate text-[11px] text-text-3">{it.meta}</dd>}
        </div>
      ))}
    </dl>
  )
}

const COLUMNS: Column<OpRow>[] = [
  { key: 'operation', header: 'Operation', mono: true, sortable: true },
  { key: 'count', header: 'Runs', mono: true, width: '110px', align: 'right', sortable: true, render: (o) => fmt(o.count) },
  {
    key: 'success', header: 'OK', mono: true, width: '110px', align: 'right', sortable: true,
    render: (o) => <span className={o.success > 0 ? 'text-ok' : 'text-text-3'}>{fmt(o.success)}</span>,
  },
  {
    key: 'failed', header: 'Failed', mono: true, width: '110px', align: 'right', sortable: true,
    render: (o) => <span className={o.failed > 0 ? 'text-danger' : 'text-text-3'}>{fmt(o.failed)}</span>,
  },
  {
    key: 'avg_latency_ms', header: 'Avg latency', mono: true, width: '130px', align: 'right', sortable: true,
    render: (o) => fmtDuration(o.avg_latency_ms),
  },
]

function InsightsBody({ stats, sort, onSort }: { stats: AuditStats; sort: Sort; onSort: (k: string) => void }) {
  const t = stats.totals
  const successRate = t.total > 0 ? Math.round(((t.by_status.success ?? 0) / t.total) * 100) : null
  const failed = t.by_status.failed ?? 0
  const lat = stats.latency_ms

  const rows = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1
    const k = sort.key as SortKey
    return [...stats.top_operations].sort((a, b) => {
      if (k === 'operation') return a.operation.localeCompare(b.operation) * dir
      return (((a[k] ?? -1) as number) - ((b[k] ?? -1) as number)) * dir
    })
  }, [stats, sort])

  return (
    <>
      <StatRow
        items={[
          { label: 'Total runs', value: fmt(t.total), meta: `${fmt(t.real)} real · ${fmt(t.dry_run)} dry run` },
          {
            label: 'Success rate', value: successRate == null ? '—' : `${successRate}%`,
            meta: `${fmt(t.by_status.success ?? 0)} of ${fmt(t.total)} succeeded`, tone: 'text-ok',
          },
          { label: 'Failed', value: fmt(failed), meta: 'ended in error', tone: failed > 0 ? 'text-danger' : undefined },
          {
            label: 'Silent failures', value: fmt(t.silent_failure), meta: 'Atlassian no-op',
            tone: t.silent_failure > 0 ? 'text-warn' : undefined,
          },
          { label: 'Critical', value: fmt(t.critical), meta: 'critical operations' },
          {
            label: 'Latency avg', value: fmtDuration(lat?.avg),
            meta: lat ? `p95 ${fmtDuration(lat.p95)} · max ${fmtDuration(lat.max)}` : 'no finished runs',
          },
        ]}
      />
      <div className="flex min-h-10 shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b border-line px-4 py-1.5 text-[12px]">
        <span className="text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">By status</span>
        {Object.entries(t.by_status).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
          <span key={s} className="font-mono">
            <span className={statusTone(s)}>{s}</span> <span className="text-text-2">{fmt(n)}</span>
          </span>
        ))}
        <span aria-hidden className="h-3 w-px bg-line-strong" />
        <span className="text-[11px] font-medium uppercase tracking-[0.07em] text-text-3">By surface</span>
        {Object.entries(t.by_surface).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
          <span key={s} className="font-mono text-text-2">{s} <span className="text-foreground">{fmt(n)}</span></span>
        ))}
        {stats.window.truncated && (
          <span className="ml-auto font-mono text-[11px] text-text-3">most recent {fmt(stats.window.scanned)} runs</span>
        )}
      </div>
      <DataTable
        label="Runs per operation"
        rows={rows}
        columns={COLUMNS}
        rowId={(o) => o.operation}
        sort={sort}
        onSort={onSort}
        empty="No operations in range."
      />
    </>
  )
}

export default function AuditInsights() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [debouncedQ, setDebouncedQ] = useState('')
  const [stats, setStats] = useState<AuditStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [sort, setSort] = useState<Sort>({ key: 'count', dir: 'desc' })
  const reqId = useRef(0)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 300)
    return () => clearTimeout(t)
  }, [filters.q])

  const effective = useMemo<Filters>(() => ({ ...filters, q: debouncedQ }), [filters, debouncedQ])

  useEffect(() => {
    const id = ++reqId.current
    setLoading(true)
    fetchAuditStats(effective)
      .then((s) => {
        if (id === reqId.current) {
          setStats(s)
          setLoading(false)
        }
      })
      .catch(() => {
        if (id === reqId.current) {
          setStats(null)
          setLoading(false)
        }
      })
  }, [effective])

  const onSort = (k: string) => setSort((s) => (s.key === k
    ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' }
    : { key: k, dir: k === 'operation' ? 'asc' : 'desc' }))

  return (
    <Page
      title="Audit"
      hint="Aggregate signal across the operations log — volume, outcomes and latency for the current filter."
      context={<AuditTabs />}
    >
      <FilterBar value={filters} onChange={setFilters} />
      {loading
        ? <LoadingRows label="Loading insights" />
        : !stats || !stats.totals || stats.totals.total === 0
          ? <p className="px-4 py-4 text-[13px] text-text-2">No audit activity in range.</p>
          : (
            <SectionBoundary label="Insights" resetKeys={[stats]}>
              <InsightsBody stats={stats} sort={sort} onSort={onSort} />
            </SectionBoundary>
          )}
    </Page>
  )
}
